import { flow, ReadonlyArray, Runtime } from "fp";

import { Company } from "~/Company";
import * as Data from "~/Data";
import { Request } from "~/Request";

import { QueryKey } from "react-query";
import { Filter, Pairing } from "~/Driver";
import { Time } from "~/Time";

export namespace Query {
  export const decode = (
    response: Runtime.ToType<typeof Pairing.Response.runtimeArray>
  ): Record<string, readonly Pairing.Pairing[]> =>
    pipe(
      response,
      ReadonlyArray.reduce(
        {} as Record<string, readonly Pairing.Pairing[]>,
        (reduction, data) =>
          pipe(Pairing.Response.decode(data), (pairing) => ({
            ...reduction,
            [pairing.driver.id]: [
              ...(reduction[pairing.driver.id] ?? []),
              pairing,
            ],
          }))
      )
    );

  export namespace Driver {
    export const tag = () => "Pairing History - Driver" as const;

    export const key = ({
      driver,
      dateTime,
    }: Partial<Time.DateTime> & {
      readonly driver: Data.Reference.Reference;
    }): QueryKey => [
      tag(),
      `${driver.id}-${
        dateTime
          ? Time.DateTime.toISOLocalWithoutTime({ dateTime })
          : Time.DateTime.toISOLocalWithoutTime(Time.DateTime.now())
      }`,
    ];

    export const query = ({
      driver,
      dateTime,
    }: Partial<Time.DateTime> & {
      readonly driver: Data.Reference.Reference;
    }): Request.Fn<ReturnType<typeof decode>> => {
      const endDate = dateTime
        ? Time.DateTime.toISOLocalWithoutTime(
            Time.DateTime.add({ dateTime })(1, "days")
          )
        : "";
      const startDate = dateTime
        ? Time.DateTime.toISOLocalWithoutTime(
            Time.DateTime.subtract({ dateTime })(1, "days")
          )
        : "";
      const queryParams = dateTime
        ? `?start_date=${startDate}&end_date=${endDate}`
        : "";
      return flow(
        Request.fromConfig({
          endpoint: `/elogs/drivers/${driver.id}/driver-asset-pairing-history${queryParams}`,
        }),
        Request.Expect.status(200),
        Request.Expect.json(Pairing.Response.runtimeArray()),
        Request.onSuccess(decode)
      );
    };
  }

  export namespace Company {
    export type RequestProps = Company & {
      readonly reportingLocationIds?: Filter.Filter;
      readonly dateRange?: Time.Interval.Stopped;
    };

    export const tag = () => "Pairing History - Company" as const;

    export const key = ({
      reportingLocationIds,
      dateRange,
    }: {
      readonly reportingLocationIds?: Filter.Filter;
      readonly dateRange?: Time.Interval.Stopped;
    }): QueryKey => [
      tag(),
      dateRange
        ? `${Time.DateTime.toISOWithoutTime(
            dateRange.interval.start
          )},${Time.DateTime.toISOWithoutTime(dateRange.interval.stop)}`
        : "no-interval",
      reportingLocationIds ?? [],
    ];

    export const query = ({
      company,
      reportingLocationIds,
      dateRange,
    }: RequestProps): Request.Fn<ReturnType<typeof decode>> => {
      return flow(
        Request.fromConfig({
          endpoint: `/elogs/companies/${
            company.id
          }/driver-asset-pairing-history${queryParams(
            reportingLocationIds,
            dateRange
          )}`,
        }),
        Request.Expect.status(200),
        Request.Expect.json(Pairing.Response.runtimeArray()),
        Request.onSuccess(decode)
      );
    };

    export const queryParams = (
      reportingLocationIds?: Filter.Filter,
      dateRange?: Time.Interval.Stopped
    ): string => {
      let params = "?most_recent_pairing_only=True";
      if (reportingLocationIds && reportingLocationIds.length > 0) {
        params += `&reporting_location_ids=${reportingLocationIds.join(",")}`;
      }
      if (dateRange) {
        params += `&start_date=${Time.DateTime.toISO(
          dateRange.interval.start
        )}&end_date=${Time.DateTime.toISO(dateRange.interval.stop)}`;
      }

      return params;
    };
  }
}
